import { Component, Input, OnInit } from '@angular/core';
import { ConfigService } from '../../../../../../service/config.service';
import { MatDialog, MatDialogConfig } from '@angular/material';
import { AddFundPopupComponent } from '../../../add-fund-popup/add-fund-popup.component';
import { LenderBasicInfo } from '../../../../../../model/lender.model';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ProductAttributes, ProductCode, ProductList, ProductListForCreatePortfolio } from '../../../../../../model/finzy-flex/product-list.model';
import { AutoPortfolioAllocationComponent } from '../auto-portfolio-allocation/auto-portfolio-allocation.component';
import { AllocationErrorPopupComponent } from '../allocation-error-popup/allocation-error-popup.component';
import { InvestmentService } from '../../../../../../service/finzy-flex/investment/investment.service';
import { Router } from '@angular/router';
import { PortfolioDataForCreation } from '../../../../../../model/finzy-flex/portfolio.model';
import { Keys, StorageService } from '../../../../../../service/storage.service';
import { ConsentType } from '../../../../../../model/ConsentAudit';
import { FfEndPoints } from '../../../ff-endpoints';
import { formatNumber } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FiftyLakhsErrorPopupComponent } from '../fifty-lakhs-error-popup/fifty-lakhs-error-popup.component';

@Component({
  selector: 'app-fund-allocation',
  templateUrl: './fund-allocation.component.html',
  styleUrls: ['./fund-allocation.component.css']
})
export class FundAllocationComponent implements OnInit {
  private readonly configService: ConfigService;
  private readonly storageService: StorageService;
  private readonly matDialog: MatDialog;
  private readonly autoPortfolioService: InvestmentService;
  private readonly router: Router;
  private walletBalance: number;
  private differenceAmount: number;

  public productsFormGroup: FormGroup;
  public readonly formArray: FormArray;
  public formBuilder: FormBuilder;
  public isMultipleOfThousandError = false;
  public isInvestmentAmountExceededError = false;
  public productNameArray = [];
  public remainingWalletBalance: number;
  public totalAmountInvested: number;
  public btnLoader: boolean = false;
  public initialFundAllocationMinAmt: string = '0';
  public postFundAllocationMinAmt: string = '0';
  public maximumInvestmentAmount: number; // maximum investment limit for all products


  @Input() lenderBasicInfo: LenderBasicInfo;
  @Input() productsData: ProductList;
  @Input() flexInvestmentAmount: number;
  @Input() maxInvestmentAmount: number; // max investment amount investor can do

  constructor(
    configService: ConfigService,
    storageService: StorageService,
    autoPortfolioService: InvestmentService,
    matDialog: MatDialog,
    router: Router,
    formBuilder: FormBuilder,
  ) {
    this.configService = configService;
    this.storageService = storageService;
    this.autoPortfolioService = autoPortfolioService;
    this.matDialog = matDialog;
    this.router = router;
    this.formBuilder = formBuilder;
    this.productsFormGroup = this.formBuilder.group({ 'lenderExposureAcrossP2PPlatforms': null, 'products': null });
    this.initialFundAllocationMinAmt = formatNumber(this.configService.getInitialFundAllocationMinAmount(), "en-IN", "1.0-2");
    this.postFundAllocationMinAmt = formatNumber(this.configService.getFundAllocationMinAmount(), "en-IN", "1.0-2");
    this.maximumInvestmentAmount = this.configService.getMaximumInvestmentLimit();
  }

  public async ngOnInit() {
    this.lenderBasicInfo.portfolioPresent = this.lenderBasicInfo.portfolioPresent ?? true;
    this.walletBalance = this.lenderBasicInfo.walletAmount;
    this.productsFormGroup = this.formBuilder.group({
      'lenderExposureAcrossP2PPlatforms': this.formBuilder.control(false, Validators.requiredTrue),
      'products': this.formBuilder.array(
        [],
        [
          this.customValidateFormArray(),
          this.anyOneInputShouldHaveValue(),
          (this.lenderBasicInfo?.portfolioPresent) ? this.isNotFirstPortfolio() : this.isFirstPortfolio(),
        ]
      )
    });
    this.populateForm();
  }

  private customValidateFormArray(): ValidatorFn {
    return (formArray: FormArray): any => {
      let sumAmount = 0;
      let controllerValid: boolean = true;
      let shouldContainPositiveValue = true;
      let isMultipleOfThousand = true;
      formArray.controls.forEach((element: FormControl) => {
        if (element.value.investmentAmount < 0) {
          shouldContainPositiveValue = false;
        }
        if ((element.value.investmentAmount % 1000) != 0) {
          this.isMultipleOfThousandError = true;
          isMultipleOfThousand = false;
        }
        sumAmount = sumAmount + Number(element.value.investmentAmount);
        this.totalAmountInvested = sumAmount;
      });
      if (this.totalAmountInvested > this.maxInvestmentAmount) {
        this.isInvestmentAmountExceededError = true;
        controllerValid = false;
        return controllerValid ? null : { error: 'investmentAmountExceededError' };
      }
      if (!isMultipleOfThousand) {
        controllerValid = false;
        return controllerValid ? null : { error: 'multipleOfThousandError' };
      }
      if (!shouldContainPositiveValue) {
        controllerValid = false;
        return controllerValid ? null : { error: 'negativeNumberError' }
      } else {
        controllerValid = (sumAmount <= this.walletBalance) ? true : false;
        this.remainingWalletBalance = this.walletBalance - sumAmount;
        return controllerValid ? null : { error: 'walletAmountExceededError' };
      }
    };
  }

  private anyOneInputShouldHaveValue(): ValidatorFn {
    return (formArray: FormArray): any => {
      let sumAmount = 0;
      let controllerValid: boolean = true;
      formArray.controls.forEach((element: FormControl) => {
        sumAmount = sumAmount + Number(element.value.investmentAmount);
      });
      controllerValid = (sumAmount > 0) ? true : false;
      return controllerValid ? null : { error: 'fieldEmptyError' };
    };
  }

  private multipleOfThousand(controlName: string) {
    return (formGroup: FormGroup): any => {
      let controllerValid: boolean = true;
      const inputValue: number = formGroup.controls[controlName].value;
      controllerValid = (inputValue % 1000 == 0) ? true : false;
      this.isMultipleOfThousandError = (inputValue % 1000 == 0) ? false : true;
      return controllerValid ? null : { error: 'multipleOfThousandError' };
    };
  }

  private isNotFirstPortfolio(): ValidatorFn {
    return (formArray: FormArray): any => {
      let sumAmount = 0;
      let controllerValid: boolean = true;
      formArray.controls.forEach((element: FormControl) => {
        sumAmount = sumAmount + Number(element.value.investmentAmount);
      });
      // Difference of amount to meet initial investment amount and flooring to next nearest 1000
      const diffFundAlloMinAmount = Math.ceil((this.configService.getInitialFundAllocationMinAmount() - this.flexInvestmentAmount) / 1000) * 1000;
      const minAmountToInvest = this.flexInvestmentAmount - this.configService.getInitialFundAllocationMinAmount() >= 0
        ? this.configService.getFundAllocationMinAmount()
        : diffFundAlloMinAmount <= this.configService.getFundAllocationMinAmount()
          ? this.configService.getFundAllocationMinAmount()
          : diffFundAlloMinAmount;
      controllerValid = (sumAmount >= minAmountToInvest) ? true : false;
      this.postFundAllocationMinAmt = minAmountToInvest.toString();
      this.differenceAmount = minAmountToInvest - sumAmount;
      if (this.remainingWalletBalance < 0) {
        return controllerValid = null;
      } else {
        return controllerValid ? null : { error: 'notFirstPortfolioError' };
      }
    };
  }

  private isFirstPortfolio(): ValidatorFn {
    return (formArray: FormArray): any => {
      let sumAmount = 0;
      let controllerValid: boolean = true;
      formArray.controls.forEach((element: FormControl) => {
        sumAmount = sumAmount + Number(element.value.investmentAmount);
      });
      const minAmountToInvest = this.configService.getInitialFundAllocationMinAmount();
      controllerValid = (sumAmount >= minAmountToInvest) ? true : false;
      this.differenceAmount = minAmountToInvest - sumAmount;
      if (this.remainingWalletBalance < 0) {
        return controllerValid = null;
      } else {
        return controllerValid ? null : { error: 'firstPortfolioError' };
      }
    };
  }

  private checkDateInRange(fromDate: string, toDate: string): boolean {
    if (new Date(fromDate).getTime() <= new Date().getTime() &&
      new Date().getTime() <= new Date(toDate).getTime()) {
      return true;
    } else {
      return false;
    }
  }

  private populateForm(): void {
    this.productNameArray = [];
    this.productsData?.data.forEach((product) => {
      const fromDate = product.productVersions[0]?.startDate;
      const toDate = product.productVersions[0]?.endDate;
      if (this.checkDateInRange(fromDate, toDate)) {
        this.productNameArray.push(
          {
            name: product.productVersions[0].productAttributes.find(attribute => attribute.code == 'NAME') ? product.productVersions[0].productAttributes.find(attribute => attribute.code == 'NAME').value.toString() : product.name,
            code: product.code,
            version: product.productVersions[0].productVersion,
            mhp: product.productVersions[0].productAttributes.find(attribute => attribute.code == ProductCode.MHP_FRONTEND_DISPLAY) ? product.productVersions[0].productAttributes.find(attribute => attribute.code == ProductCode.MHP_FRONTEND_DISPLAY).value : 0,
            roi: product.productVersions[0].productAttributes.find(attribute => attribute.code == ProductCode.ROI) ? product.productVersions[0].productAttributes.find(attribute => attribute.code == ProductCode.ROI).value : 0,
            maturity_days: product.productVersions[0].productAttributes.find(attribute => attribute.code === ProductCode.MATURITY_DAYS)?.value,
          }
        );
        this.productList().push(
          this.formBuilder.group({
            investmentAmount: this.formBuilder.control(
              '',
              // [
              //   Validators.min(
              //     this.findByKey('MIN_AUTO_PORTFOLIO_AMOUNT', product.productVersions[0].productAttributes)
              //   ),
              //   Validators.max(
              //     this.findByKey('MAX_PORTFOLIO_AMOUNT', product.productVersions[0].productAttributes)
              //   )
              // ]
            ),
          }, {
            validator: this.multipleOfThousand('investmentAmount'),
          }
          )
        );
      }
    });
  }

  private findByKey(key: string, data: ProductAttributes[]): number {
    const returnData = data.find(element => element.code == key).value;
    return returnData;
  }

  private currencyInIndianFormat(value: number) {
    const currency = value.toString();
    var lastThreeDigit = currency.substring(currency.length - 3);
    var otherDigits = currency.substring(0, currency.length - 3);
    if (otherDigits != '') {
      lastThreeDigit = ',' + lastThreeDigit;
    }
    const result = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThreeDigit;
    return result;
  }

  public productList(): FormArray {
    return this.productsFormGroup.get('products') as FormArray;
  }

  public getAmountAvailableToInvest(): number {
    this.walletBalance = this.lenderBasicInfo?.walletAmount;
    return this.walletBalance;
  }

  public onAddMoneyToWallet(): void {
    this.storageService.saveItem(Keys.PARENT_WALLET_PATH, this.router.url);
    const initialFundAllocationMinAmt = this.configService.getInitialFundAllocationMinAmount();
    const postFundAllocationMinAmount = this.configService.getFundAllocationMinAmount();
    const fundAllocationMinAmount = this.lenderBasicInfo.portfolioPresent ? postFundAllocationMinAmount : initialFundAllocationMinAmt;
    const matDialogConfig: MatDialogConfig = {
      height: '90%',
      width: '80%',
      panelClass: "dialog-responsive",
      data: { virtualAccountNumber: this.lenderBasicInfo?.virtualAccountNumber, fundAllocationMinimumAmount: fundAllocationMinAmount }
    };
    this.matDialog.open(AddFundPopupComponent, matDialogConfig);
  }

  private openConfirmAutoPortfolioCreationPopup(data: FormGroup) {
    const productListForCreatePortfolio: ProductListForCreatePortfolio[] = [];
    const consent = data.value.lenderExposureAcrossP2PPlatforms;
    for (let i = 0; i < data.value.products.length; i++) {
      const productsDetails: ProductListForCreatePortfolio = {
        productName: this.productNameArray[i].name,
        productCode: this.productNameArray[i].code,
        investmentAmount: data.value.products[i].investmentAmount,
        productVersion: this.productNameArray[i].version,
        consents: [
          {
            consentType: ConsentType.LENDER_EXPOSURE_ACROSS_P2P_PLATFORMS,
            value: this.productsFormGroup.controls.lenderExposureAcrossP2PPlatforms.value,
          }
        ]
      };
      productListForCreatePortfolio.push(productsDetails);
    }
    const matDialogConfig: MatDialogConfig = {
      height: 'auto',
      width: '80%',
      panelClass: 'auto-portfolio-confirmation-popup',
      data: {
        products: productListForCreatePortfolio,
        lenderExposureAcrossP2PPlatforms: consent,
        remainingWalletBalance: this.remainingWalletBalance,
      }
    };
    const filteredDataForPortfolioCreation = productListForCreatePortfolio.filter((element) => element.investmentAmount > 0);
    const portfolioCreationData: PortfolioDataForCreation = {
      investmentAmount: this.totalAmountInvested,
      portfolioInvestments: filteredDataForPortfolioCreation,
    };
    return this.matDialog.open(AutoPortfolioAllocationComponent, matDialogConfig)
      .afterClosed()
      .subscribe(async (result) => {
        if (result == true) {
          try {
            this.btnLoader = true;
            this.autoPortfolioService.createAutoPortfolio(this.lenderBasicInfo?.id, portfolioCreationData)
              .then((response) => {
                if (response.status == 0 || response.count > 0) {
                  this.btnLoader = false;
                  this.router.navigateByUrl(FfEndPoints.PORTFOLIO_CREATION_SUCCESS, { state: { response } });
                } else {
                  this.btnLoader = false;
                  this.router.navigate([FfEndPoints.PORTFOLIO_CREATION_FAILURE]);
                }
              })
              .catch((error: HttpErrorResponse) => {
                if (error && error.error && error.error.data[0]) {
                  const { investmentAmount, investmentLimit } = error.error.data[0];
                  this.openFiftyLakhsErrorPopup(investmentAmount, investmentLimit);
                } else {
                  this.btnLoader = false;
                  this.router.navigate([FfEndPoints.PORTFOLIO_CREATION_FAILURE]);
                }
                console.log(error)
              })
          } catch (error) {
            this.btnLoader = false;
            this.router.navigate([FfEndPoints.PORTFOLIO_CREATION_FAILURE]);
          }
        }
      });
  }

  private openErrorPopup(differenceAmount: number, headingText: string, bodyText: string, buttonName: string[], sendDataToWallet: boolean) {
    const matDialogConfig: MatDialogConfig = {
      height: 'auto',
      panelClass: 'allocation-error-popup',
      data: {
        differenceAmount: differenceAmount,
        headingText: headingText,
        bodyText: bodyText,
        buttonName: buttonName,
        lenderBasicInfo: this.lenderBasicInfo,
        sendDataToWallet: sendDataToWallet
      }
    };
    return this.matDialog.open(AllocationErrorPopupComponent, matDialogConfig);
  }

  private openFiftyLakhsErrorPopup(investmentAmount: number, investmentLimit: number) {
    const matDialogConfig: MatDialogConfig = {
      height: 'auto',
      width: '80%',
      panelClass: 'fifty-lakhs-error-popup',
      data: {
        investmentAmount: investmentAmount,
        investmentLimit: investmentLimit,
      }
    };

    this.matDialog.open(FiftyLakhsErrorPopupComponent, matDialogConfig)
      .afterClosed().subscribe((res) => {
        this.btnLoader = false;
      })
  }

  public onSubmitToInvest(data: FormGroup) {
    data.markAllAsTouched();
    if (this.productsFormGroup.valid) {
      this.openConfirmAutoPortfolioCreationPopup(data);
    } else if (data.controls.products.errors?.error) {
      switch (data.controls.products?.errors?.error) {
        case 'notFirstPortfolioError':
          let differenceAmount = this.differenceAmount;
          let headingText = "Insufficient Wallet Balance";
          let bodyText = "You can start investing with a minimum of  ₹<strong class='text-dark'>10,000</strong> and in multiples of ₹<strong class='text-dark'>1,000</strong> thereon.";
          let buttonName = ['BACK', 'ADD MONEY'];
          this.openErrorPopup(differenceAmount, headingText, bodyText, buttonName, false);
          break;
        case 'firstPortfolioError':
          headingText = "Insufficient Wallet Balance";
          differenceAmount = this.differenceAmount;
          bodyText = "You can start investing with a minimum of  ₹<strong class='text-dark'>50,000</strong> and in multiples of ₹<strong class='text-dark'>1,000</strong> thereon.";
          buttonName = ['BACK', 'ADD MONEY'];
          this.openErrorPopup(differenceAmount, headingText, bodyText, buttonName, false);
          break;
        case 'walletAmountExceededError':
          headingText = "Insufficient Wallet Balance"
          differenceAmount = Math.abs(this.remainingWalletBalance);
          bodyText = "Your available wallet balance is lesser than the allocation amount. You can choose to <strong class='text-dark'> add money </strong> or <strong class='text-dark'> change allocation </strong>";
          buttonName = ['CHANGE ALLOCATION', 'ADD MONEY']
          this.openErrorPopup(differenceAmount, headingText, bodyText, buttonName, true);
          break;
        case 'investmentAmountExceededError':
          this.openFiftyLakhsErrorPopup((this.maximumInvestmentAmount-this.maxInvestmentAmount), this.maximumInvestmentAmount)
          break;
        default: break;
      }
    }
  }

  public disableAnySymbols(event) {
    return (event.charCode != 8 && event.charCode == 0 || (event.charCode >= 48 && event.charCode <= 57));
  }

  public minimumFirstTimePortfolioAmount(): Number {
    return this.configService.getInitialFundAllocationMinAmount();
  }
  public getFileUrlByName(fileName):string{
    return this.configService.getPolicyFileUrlByName(fileName);
  }

}